import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import type { Proforma } from "@/lib/proforma.api";
import { cn } from "@/lib/utils";
import { getProformaTotal } from "../proforma-calculations";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  getLineTypeLabel,
} from "../proforma-formatters";
import { MiniStat } from "./ProformaMiniFacts";
import { ProformaStatusBadge } from "./ProformaStatusBadge";

interface ProformaQuickViewDialogProps {
  proforma: Proforma | null;
  onOpenChange: (open: boolean) => void;
}

export function ProformaQuickViewDialog({
  proforma,
  onOpenChange,
}: ProformaQuickViewDialogProps) {
  const total = proforma ? getProformaTotal(proforma) : 0;

  return (
    <Dialog open={Boolean(proforma)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-3xl">
        {proforma && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    {proforma.number}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {proforma.partySnapshot?.fullName ?? "Client non renseigné"} · créée le{" "}
                    {formatDateTime(proforma.createdAt)}
                  </DialogDescription>
                </div>
                <ProformaStatusBadge status={proforma.status} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <MiniStat label="Total" value={formatMoney(total)} />
              <MiniStat label="Lignes" value={String(proforma.lines.length)} />
              <MiniStat label="Créée" value={formatDate(proforma.createdAt)} />
              <MiniStat label="Expiration" value={formatDate(proforma.expiresAt)} />
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border bg-surface-muted px-4 py-2">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Contenu de la proforma
                </p>
              </div>
              <div className="divide-y divide-neutral-100">
                {proforma.lines.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[12px] text-muted-foreground">
                    Aucune ligne.
                  </p>
                ) : (
                  proforma.lines.map((line) => (
                    <div
                      key={line.id}
                      className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[12px] font-medium text-foreground">
                          {line.description}
                        </p>
                        <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                          {getLineTypeLabel(line.lineType)} · quantité {line.quantity} · unité{" "}
                          {formatMoney(line.unitPrice)}
                        </p>
                      </div>
                      <p className="text-[12px] font-semibold tabular-nums text-foreground">
                        {formatMoney(line.lineTotal)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              <a
                href={`/api/proformas/${proforma.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants({ variant: "outline" }))}
              >
                PDF
              </a>
              <Link to={`/proformas/${proforma.id}`} className={cn(buttonVariants())}>
                Voir le détail
              </Link>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
